import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Users, Sparkles, Plus, X, Heart } from 'lucide-react';
import api from '../lib/api';
import { useSubscription } from '../context/SubscriptionContext';

const ROLE_META = {
  grandparent:   { emoji: '👵', label: 'Grandparent' },
  uncle:         { emoji: '👨', label: 'Uncle' },
  aunt:          { emoji: '👩', label: 'Aunt' },
  family_friend: { emoji: '🤝', label: 'Family friend' },
};

const InviteCrewModal = ({ onClose, onInvited }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('grandparent');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setBusy(true);
    try {
      await api.post('/child/invite-crew', { invitee_name: name.trim(), invitee_email: email.trim(), member_role: role });
      toast.success('Sent to your parent for approval ✨');
      onInvited();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-navy/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="invite-crew-modal">
      <div className="bg-white rounded-3xl w-full max-w-md shadow-squishyLg overflow-hidden">
        <div className="bg-gradient-to-br from-teal to-navy p-5 text-white flex items-start justify-between">
          <div>
            <div className="text-gold text-[10px] font-heading font-bold uppercase tracking-wider">Support Crew</div>
            <h2 className="font-heading text-xl font-bold leading-tight">Invite Someone Special</h2>
            <p className="text-white/85 text-xs mt-1">Grandparents, uncles, aunts and family friends can cheer you on. A parent must approve.</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white" aria-label="Close" data-testid="close-invite-crew"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <div>
            <label className="block text-[10px] font-heading font-bold text-navy/70 uppercase mb-1">Who are they?</label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(ROLE_META).map(([key, m]) => (
                <button type="button" key={key} onClick={() => setRole(key)}
                  className={`p-3 rounded-2xl border-2 text-left flex items-center gap-2 transition-colors ${role === key ? 'border-teal bg-teal/10' : 'border-slate-200 hover:border-teal/50'}`}
                  data-testid={`crew-role-${key}`}>
                  <span className="text-2xl">{m.emoji}</span>
                  <span className="font-heading font-bold text-navy text-sm">{m.label}</span>
                </button>
              ))}
            </div>
          </div>
          <input type="text" required placeholder="Their name (e.g. Grandma Rose)" value={name} onChange={(e) => setName(e.target.value)}
            className="w-full h-12 px-4 rounded-2xl border-2 border-slate-200 focus:border-teal focus:outline-none font-body text-sm"
            data-testid="crew-name-input" />
          <input type="email" required placeholder="Their email" value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full h-12 px-4 rounded-2xl border-2 border-slate-200 focus:border-teal focus:outline-none font-body text-sm"
            data-testid="crew-email-input" />
          <button type="submit" disabled={busy}
            className="w-full h-12 rounded-full bg-gold text-navy font-heading font-bold shadow-gold disabled:opacity-60 inline-flex items-center justify-center gap-2"
            data-testid="submit-crew-invite">
            <Sparkles className="w-4 h-4" /> {busy ? 'Sending…' : 'Ask my parent'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default function SupportCrew({ childName }) {
  const [crew, setCrew] = useState([]);
  const [reactions, setReactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const { requirePremium } = useSubscription();

  const load = async () => {
    try {
      const [c, r] = await Promise.all([
        api.get('/child/support-crew'),
        api.get('/child/reactions'),
      ]);
      setCrew(c.data.crew || []);
      setReactions(r.data.reactions || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const approved = crew.filter((c) => c.status === 'approved' && c.joined);
  const pending = crew.filter((c) => c.status === 'pending');
  const invited = crew.filter((c) => c.status === 'approved' && !c.joined);

  return (
    <section className="bg-white rounded-3xl p-6 shadow-squishy border-2 border-cream" data-testid="support-crew-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-teal" />
          <h2 className="font-heading text-xl font-bold text-navy">My Support Crew</h2>
        </div>
        <button onClick={() => { if (requirePremium('crew')) setShowInvite(true); }}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-navy text-white font-heading font-bold text-xs hover:scale-[1.03] transition-transform"
          data-testid="invite-crew-button">
          <Plus className="w-3.5 h-3.5" /> Invite Someone Special
        </button>
      </div>

      {/* Constellation visual */}
      {approved.length > 0 ? (
        <div className="relative py-4 mb-3 min-h-[110px] flex items-center justify-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-gold to-teal flex items-center justify-center font-heading font-bold text-white text-lg shadow-gold z-10 animate-float" data-testid="crew-center">
            {(childName || 'me')[0]?.toUpperCase()}
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative" style={{ width: 260, height: 110 }}>
              {approved.map((m, i) => {
                const total = approved.length;
                const angle = (Math.PI * (i + 0.5)) / (total + 1);
                const cx = 130 + Math.cos(angle) * 110 - 22;
                const cy = 55 - Math.sin(angle) * 46 - 22;
                return (
                  <div key={m.id}
                    className="absolute w-11 h-11 rounded-full bg-cream border-2 border-teal flex items-center justify-center text-xl shadow-squishy"
                    style={{ left: cx, top: cy }}
                    title={`${m.member_name} · ${m.role_label}`}
                    data-testid={`crew-avatar-${m.id}`}>
                    {m.role_emoji}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border-2 border-dashed border-teal/40 bg-teal/5 p-4 text-center mb-3">
          <p className="text-navy font-heading font-bold text-sm">Build your crew!</p>
          <p className="text-navy/60 text-xs mt-1">Invite grandparents, uncles, aunts or family friends to cheer you on.</p>
        </div>
      )}

      {(pending.length > 0 || invited.length > 0) && (
        <div className="space-y-2 mb-3">
          {pending.map((m) => (
            <div key={m.id} className="flex items-center gap-2 text-xs text-navy/70 px-2" data-testid={`crew-pending-${m.id}`}>
              <span className="text-lg">{m.role_emoji}</span>
              <span><span className="font-heading font-bold">{m.member_name}</span> · waiting for parent approval…</span>
            </div>
          ))}
          {invited.map((m) => (
            <div key={m.id} className="flex items-center gap-2 text-xs text-navy/70 px-2" data-testid={`crew-invited-${m.id}`}>
              <span className="text-lg">{m.role_emoji}</span>
              <span><span className="font-heading font-bold">{m.member_name}</span> · approved — waiting for them to join</span>
            </div>
          ))}
        </div>
      )}

      {reactions.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 text-xs font-heading font-bold text-navy/70 uppercase tracking-wider mb-2">
            <Heart className="w-3.5 h-3.5 text-red-400 fill-current" /> Cheers you received
          </div>
          <div className="space-y-1.5">
            {reactions.slice(0, 4).map((r) => (
              <div key={r.id} className="flex items-center gap-2 text-sm p-2 rounded-xl bg-cream" data-testid={`crew-reaction-${r.id}`}>
                <span className="text-xl">{r.role_emoji}</span>
                <span className="text-navy"><span className="font-heading font-bold">{r.member_name}</span> sent you a <span className="text-lg">{r.emoji}</span></span>
              </div>
            ))}
          </div>
        </div>
      )}

      {showInvite && <InviteCrewModal onClose={() => setShowInvite(false)} onInvited={() => { setShowInvite(false); load(); }} />}
    </section>
  );
}
